import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useLogin } from '@/features/auth/useAuth'
import { fetchWorkspaces } from '@/api/auth'
import type { ApiError } from '@/api/client'

export default function Login() {
  const login = useLogin()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [slug, setSlug] = useState('')

  // When an email spans several workspaces, fetch the ones this password unlocks and let the user pick.
  const wsList = useMutation({
    mutationFn: (v: { email: string; password: string }) => fetchWorkspaces(v.email, v.password),
  })
  const workspaces = wsList.data ?? []

  const error = login.error as ApiError | null
  const needsWorkspace = error?.code === 'auth.workspace.required'
  const showWorkspace = needsWorkspace || slug.trim() !== '' || workspaces.length > 0

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    login.mutate(
      { email, password, slug: slug.trim() || undefined },
      {
        onError: (err) => {
          // On the multi-workspace signal, load the caller's workspaces to populate the picker.
          if ((err as ApiError).code === 'auth.workspace.required' && !wsList.data && !wsList.isPending) {
            wsList.mutate({ email, password })
          }
        },
      },
    )
  }

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-sm">
        <div className="text-center mb-8">
          <h1>
            {/* Full brand logo: funnel mark + PROFITPIPE + "Your Profit Pipeline" tagline */}
            <img src="/brand/profitpipe-logo.svg" alt="ProfitPipe — Your Profit Pipeline" className="w-56 h-auto mx-auto" />
          </h1>
        </div>

        <form onSubmit={onSubmit}>
          <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
          <input
            type="email"
            value={email}
            placeholder="you@agency.com"
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mb-4 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
          />
          <label className="block text-xs font-medium text-gray-500 mb-1">Password</label>
          <input
            type="password"
            value={password}
            placeholder="••••••••"
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mb-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
          />
          <div className="text-right mb-6">
            <Link to="/forgot-password" className="text-xs text-brand-primary hover:underline">
              Forgot password?
            </Link>
          </div>

          {showWorkspace && (
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 mb-1">Workspace</label>
              {workspaces.length > 0 ? (
                <select
                  value={slug}
                  autoFocus
                  onChange={(e) => setSlug(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                >
                  <option value="" disabled>Choose your workspace…</option>
                  {workspaces.map((w) => (
                    <option key={w.slug} value={w.slug}>{w.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={slug}
                  placeholder="your-agency"
                  autoComplete="off"
                  autoFocus
                  onChange={(e) => setSlug(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                />
              )}
              <p className="text-xs text-gray-400 mt-1">
                This email is in more than one workspace — {workspaces.length > 0 ? 'pick yours' : 'enter yours'} to continue.
              </p>
            </div>
          )}

          {error && (
            <p className="text-danger text-xs mb-4">
              {error.code === 'auth.email_not_verified'
                ? 'Please confirm your email first — check your inbox for the verification link.'
                : error.message}
            </p>
          )}

          <button
            type="submit"
            disabled={login.isPending}
            className="w-full bg-brand-primary text-white py-2.5 rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors disabled:opacity-60"
          >
            {login.isPending ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <p className="text-center text-xs text-gray-400 mt-6">
          No workspace yet?{' '}
          <Link to="/register" className="text-brand-primary hover:underline">
            Create one
          </Link>
        </p>
      </div>
    </div>
  )
}
